/**
 * Fixed vocabularies. These are compiled into the app because they describe the
 * shape of the experiment protocol itself.
 *
 * Lists that curators need to extend at runtime live in the database under
 * Admin / Master data instead. The arrays below for weave structures, treatments,
 * methods, illuminants and observers are only the defaults those lists start from.
 */

export const RECORD_KINDS = ['experiment', 'reference'] as const
export type RecordKind = (typeof RECORD_KINDS)[number]

export const RECORD_STATUSES = [
  'draft',
  'finished',
  'in_review',
  'published',
  'rejected',
  'trashed',
] as const
export type RecordStatus = (typeof RECORD_STATUSES)[number]

export const RECORD_VISIBILITIES = ['private', 'workspace', 'public'] as const
export type RecordVisibility = (typeof RECORD_VISIBILITIES)[number]

export const ACCOUNT_STATUSES = ['pending', 'active', 'rejected', 'suspended'] as const
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number]

export const USER_ROLES = ['researcher', 'super_user', 'admin'] as const
export type UserRole = (typeof USER_ROLES)[number]

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  researcher: 'Researcher',
  super_user: 'Super user',
  admin: 'Admin',
}

/** Super users and admins approve publications and are the only ones who may add references. */
export const isCurator = (role: UserRole | null | undefined): boolean =>
  role === 'super_user' || role === 'admin'

export const VOCAB_KINDS = [
  'waterType',
  'fiber',
  'yarnType',
  'mordant',
  'institution',
  'weaveStructure',
  'preTreatment',
  'dryingMethod',
  'extractionMethod',
  'filtration',
  'illuminant',
  'observer',
] as const
export type VocabKind = (typeof VOCAB_KINDS)[number]

export const VOCAB_STATUSES = ['active', 'inactive'] as const
export type VocabStatus = (typeof VOCAB_STATUSES)[number]

export const REVIEW_DECISIONS = ['approve', 'reject'] as const
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number]

export const WORKSPACE_ROLES = ['owner', 'member'] as const
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number]

/** Where a project stands in the one-go approval of all its experiments. */
export const PROJECT_REVIEW_STATUSES = ['none', 'in_review', 'published', 'rejected'] as const
export type ProjectReviewStatus = (typeof PROJECT_REVIEW_STATUSES)[number]

export const WEAVE_STRUCTURES = [
  'Plain weave',
  'Twill',
  'Satin',
  'Knit',
  'Felt',
  'Yarn / skein',
  'Loose fibre',
  'Other',
] as const

export const PRE_TREATMENTS = [
  'None',
  'Scoured',
  'Bleached',
  'Washed',
  'Degummed',
  'Other',
] as const

export const EXTRACTION_METHODS = [
  'Boiled',
  'Simmered',
  'Cold soak',
  'Fermented',
  'Alkaline extraction',
  'Alcohol extraction',
  'Other',
] as const

export const DRYING_METHODS = [
  'Air dry',
  'Line dry',
  'Flat dry',
  'Towel blotted',
  'Oven dry',
] as const

export const FILTRATION_METHODS = ['None', 'Cloth', 'Sieve', 'Paper filter'] as const

export const DYEBATH_AGEING = ['No', 'Overnight', '24 hours', 'Several days'] as const

export const ILLUMINANTS = ['D65', 'D50', 'A', 'C', 'F2', 'F11'] as const

export const OBSERVERS = ['2°', '10°'] as const

export const DYE_SOURCE_TYPES = [
  'Root',
  'Leaf',
  'Bark',
  'Wood',
  'Flower',
  'Fruit',
  'Seed',
  'Insect',
  'Lichen',
  'Mineral',
  'Other',
] as const

/** Prefix used when minting human-readable record codes. */
export const RECORD_CODE_PREFIX: Record<RecordKind, string> = {
  experiment: 'EXP',
  reference: 'REF',
}

/** Statuses a researcher may still edit. */
export const EDITABLE_STATUSES: RecordStatus[] = ['draft', 'finished', 'rejected']

/** Statuses that appear under "Finished experiments" in My Workspace. */
export const FINISHED_STATUSES: RecordStatus[] = ['finished', 'in_review', 'published', 'rejected']

export const RECORD_STATUS_LABELS: Record<RecordStatus, string> = {
  draft: 'Draft',
  finished: 'Finished',
  in_review: 'Awaiting approval',
  published: 'Approved',
  rejected: 'Rejected',
  trashed: 'Trashed',
}

export const VISIBILITY_LABELS: Record<RecordVisibility, string> = {
  private: 'Private',
  workspace: 'Project',
  public: 'Public Database',
}

export const VOCAB_LABELS: Record<VocabKind, { singular: string; plural: string; prefix: string }> =
  {
    waterType: { singular: 'Water type', plural: 'Water types', prefix: 'WAT' },
    fiber: { singular: 'Fibre', plural: 'Fibers', prefix: 'FIB' },
    yarnType: { singular: 'Yarn type', plural: 'Yarn types', prefix: 'YRN' },
    mordant: { singular: 'Mordant', plural: 'Mordants', prefix: 'MOR' },
    institution: { singular: 'Institution', plural: 'Institutions', prefix: 'INS' },
    weaveStructure: { singular: 'Weave structure', plural: 'Weave structures', prefix: 'WEV' },
    preTreatment: { singular: 'Pre-treatment', plural: 'Pre-treatments', prefix: 'PRE' },
    dryingMethod: { singular: 'Drying method', plural: 'Drying methods', prefix: 'DRY' },
    extractionMethod: { singular: 'Extraction method', plural: 'Extraction methods', prefix: 'EXT' },
    filtration: { singular: 'Filtration method', plural: 'Filtration methods', prefix: 'FLT' },
    illuminant: { singular: 'Illuminant', plural: 'Illuminants', prefix: 'ILL' },
    observer: { singular: 'Standard observer', plural: 'Standard observers', prefix: 'OBS' },
  }

/**
 * Vocabularies whose records keep the term's name in a text column, so renaming a
 * term has to rewrite those columns too. Drying covers both drying steps.
 */
export const NAME_VALUED_VOCAB_COLUMNS: Partial<Record<VocabKind, string[]>> = {
  weaveStructure: ['tex_weave'],
  preTreatment: ['tex_pre_treatment'],
  dryingMethod: ['mor_drying_after', 'aft_drying_method'],
  extractionMethod: ['dye_extraction_method'],
  filtration: ['dye_filtration'],
  illuminant: ['res_illuminant'],
  observer: ['res_observer'],
}
